import { createFeatureSelector, createSelector } from '@ngrx/store';
import { homeFeatureKey, HomeState } from './home.reducer';
import {
  ActionBox,
  ReferenceTypeEnum,
  StakeholderRoles,
  Task,
  TaskDetails,
  TaskDetailsStakeholders,
} from 'projects/shared/src/lib/core/interfaces';

export const getHomeState = createFeatureSelector<HomeState>(homeFeatureKey);

export const getSystemSecurityScore = createSelector(
  getHomeState,
  (state) => state.systemSecurityScore,
);

export const getSystemSecurityScoreIsLoading = createSelector(
  getHomeState,
  (state) => state.systemSecurityScoreIsLoading,
);

export const getHomeTasks = createSelector(
  getHomeState,
  ({ tasks }) => tasks ?? [],
);

export const getHomeTasksIsLoading = createSelector(
  getHomeState,
  ({ tasksIsLoading }) => tasksIsLoading ?? true,
);

export const getHomeTaskDetailsLoading = createSelector(
  getHomeState,
  ({ taskDetailsLoading }) => taskDetailsLoading ?? true,
);

export const getHomeTasksShowSidebar = createSelector(
  getHomeState,
  ({ showTaskSidebar }) => showTaskSidebar ?? false,
);

export const getHomeSelectedTaskId = createSelector(
  getHomeState,
  ({ selectedTaskId }) => selectedTaskId,
);

export const selectHomeTasksStakeholderInfo = createSelector(
  getHomeState,
  ({ taskDetails }) => {
    if (!taskDetails?.tasksStakeholders) {
      return { isCurrentUserInStakeholders: false, stakeholdersCount: 0 };
    }

    const stakeholderGroups = [
      StakeholderRoles.OWNER,
      StakeholderRoles.ACCOUNTABLE,
      StakeholderRoles.CONSULTEDS,
      StakeholderRoles.INFORMDES,
    ];

    let isCurrentUserInStakeholders = false;
    let stakeholdersCount = 0;
    stakeholderGroups.forEach((group) => {
      const groupList =
        taskDetails?.tasksStakeholders[group as StakeholderRoles];
      if (groupList && Array.isArray(groupList)) {
        stakeholdersCount += groupList.filter(
          (user) => !user.isCurrentUser,
        ).length;

        if (groupList.some((user) => user.isCurrentUser)) {
          isCurrentUserInStakeholders = true;
        }
      } else if (groupList) {
        stakeholdersCount++;
        if ((groupList as any)?.isCurrentUser) {
          isCurrentUserInStakeholders = true;
          stakeholdersCount--;
        }
      }
    });

    return { isCurrentUserInStakeholders, stakeholdersCount };
  },
);

export const getHomeTaskDetails = createSelector(
  getHomeState,
  ({ taskDetails, tasks, selectedTaskId }) => {
    const flattenedArray: any[] = [];
    Object.entries(taskDetails?.tasksStakeholders || {}).forEach(
      ([role, people]) => {
        let roleName = '';
        switch (role) {
          case StakeholderRoles.OWNER:
            roleName = 'owner';
            break;
          case StakeholderRoles.ACCOUNTABLE:
            roleName = 'accountable';
            break;
          case StakeholderRoles.INFORMDES:
            roleName = 'informed';
            break;
          case StakeholderRoles.CONSULTEDS:
            roleName = 'consultant';
            break;
        }
        if (Array.isArray(people)) {
          people.forEach((person) => {
            flattenedArray.push({
              id: person.userId,
              name: person.userName,
              depName: person.depName,
              depId: person.depId,
              roleName,
              role,
              isCurrentUser: person.isCurrentUser,
            });
          });
        } else {
          flattenedArray.push({
            id: people.userId,
            name: people.userName,
            depName: people.depName,
            depId: people.depId,
            roleName,
            role,
            isCurrentUser: people.isCurrentUser,
          });
        }
      },
    );

    const sortedArray = flattenedArray.sort((a, b) => {
      const roleOrder = ['owner', 'accountables', 'consulteds', 'informeds'];
      const aRoleIndex = roleOrder.indexOf(a.role);
      const bRoleIndex = roleOrder.indexOf(b.role);

      if (aRoleIndex !== bRoleIndex) {
        return aRoleIndex - bRoleIndex;
      }

      if (a.isCurrentUser) return -1;
      if (b.isCurrentUser) return 1;

      return 0;
    });
    const selectedTask = tasks?.find((task) => task.id === selectedTaskId);
    const normalizedTaskDetails = taskDetails
      ? {
          ...taskDetails,
          isBlockedByPrerequisites:
            taskDetails.isBlockedByPrerequisites ?? false,
          blockingPrerequisiteTasks:
            taskDetails.blockingPrerequisiteTasks ?? [],
        }
      : undefined;

    if (!normalizedTaskDetails && selectedTask) {
      return {
        ...selectedTask,
        actionBox: {} as ActionBox,
        taskId: selectedTask.id,
        taskName: selectedTask.name,
        taskStatus: selectedTask.taskStatusId,
        taskReferences: [],
        comments: [],
        taskDetailsCommentPending: false,
        taskDetailsCommentFailed: false,
        newComment: undefined,
        tasksStakeholders: {} as TaskDetailsStakeholders,
        stakeholders: [],
        remediationSteps: '',
        taskDescription: '',
        totalSimilarTasksInstatusClose: 0,
        totalSimilarTasks: 0,
        blockingPrerequisiteTasks: [],
      };
    }
    return {
      ...selectedTask,
      ...normalizedTaskDetails,
      taskReferences: [...(normalizedTaskDetails?.taskReferences || [])].sort(
        (a, b) => {
          if (a.referenceType === ReferenceTypeEnum.RISK) {
            return -1;
          } else if (b.referenceType === ReferenceTypeEnum.RISK) {
            return 1;
          } else {
            return 0;
          }
        },
      ),
      tasksStakeholders: normalizedTaskDetails?.tasksStakeholders ?? {},
      comments: normalizedTaskDetails?.comments,
      isBlockedByPrerequisites:
        normalizedTaskDetails?.isBlockedByPrerequisites ?? false,
      blockingPrerequisiteTasks:
        normalizedTaskDetails?.blockingPrerequisiteTasks ?? [],
      stakeholders: sortedArray as any,
    } as TaskDetails & Task;
  },
);

export const getHomeRisksDome = createSelector(
  getHomeState,
  ({ risksDome }) => risksDome,
);

export const getHomeRisksDomeIsLoading = createSelector(
  getHomeState,
  ({ risksDomeIsLoading }) => risksDomeIsLoading ?? false,
);
